import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime } from "@/lib/fmt";
import { Check, X, AlertTriangle, ShieldCheck } from "lucide-react";
import { RoasPanel } from "@/components/roas/RoasPanel";
import { FlagCard } from "@/components/desk/FlagCard";
import { PersonHistory } from "@/components/desk/PersonHistory";

const STATUS_CLS = { pending: "text-[var(--warn)]", approved: "text-[var(--ok)]", rejected: "text-[var(--breach)]", failed: "text-[var(--breach)]" };
const openLead = (id) => id && window.open(`/crm?lead=${id}`, "_blank");

function RequestRow({ r, admin, onDone }) {
  const [note, setNote] = useState("");
  const decide = async (ok) => {
    try { await api.post(`/requests/${r._id}/${ok ? "approve" : "reject"}`, { note }); toast.success(ok ? "Approved and applied" : "Rejected"); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't decide"); onDone(); }
  };
  return (
    <div className="py-3 border-b border-[var(--border)] last:border-0" data-testid={`request-${r._id}`}>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="text-xs uppercase tracking-wider text-[var(--text-dim)]">{r.label}</span>
        {r.lead_name && <button className="text-sm font-medium hover:underline" onClick={() => openLead(r.lead_id)}>{r.lead_name}</button>}
        <span className="text-xs text-[var(--text-faint)]">{r.requested_by} · {fmtDateTime(r.created_at)}</span>
        {r.status !== "pending" && <span className={`text-xs ${STATUS_CLS[r.status]}`}>{r.status} by {r.decided_by}{r.decision_note ? ` \u00b7 "${r.decision_note}"` : ""}</span>}
      </div>
      <div className="text-sm mt-1">{r.summary}</div>
      <div className="text-sm mt-1 text-[var(--text-dim)]">Reason: <span className="text-[var(--text)]">{r.reason}</span></div>
      {admin && r.status === "pending" && (
        <div className="flex flex-wrap gap-2 mt-2">
          <input className="px-2.5 py-1.5 rounded border text-sm flex-1 min-w-[180px]" placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid={`request-note-${r._id}`} />
          <button className="btn btn-primary text-xs" onClick={() => decide(true)} data-testid={`request-approve-${r._id}`}><Check size={12} />Approve</button>
          <button className="btn text-xs hover:text-[var(--breach)]" onClick={() => decide(false)} data-testid={`request-reject-${r._id}`}><X size={12} />Reject</button>
        </div>
      )}
    </div>
  );
}

function Requests({ admin }) {
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("pending");
  const load = () => api.get("/requests", { params: tab === "pending" ? { status: "pending" } : {} }).then((r) => setD(r.data));
  useEffect(() => {
    load();
    window.addEventListener("requests-changed", load);
    return () => window.removeEventListener("requests-changed", load);
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps
  const done = () => { load(); window.dispatchEvent(new Event("requests-changed")); };
  return (
    <section className="panel p-4" data-testid="requests-section">
      <div className="flex items-center gap-3 mb-1">
        <div className="text-sm font-medium flex-1">{admin ? "Active requests" : "My requests"} {d?.pending ? <span className="ml-1 px-1.5 rounded bg-[var(--warn)] text-black text-xs" data-testid="requests-pending-count">{d.pending}</span> : null}</div>
        {["pending", "all"].map((t) => <button key={t} className={`text-xs ${tab === t ? "text-[var(--ai)]" : "text-[var(--text-dim)]"}`} onClick={() => setTab(t)} data-testid={`requests-tab-${t}`}>{t === "pending" ? "Pending" : "History"}</button>)}
      </div>
      {!d ? <div className="text-xs text-[var(--text-dim)]">Loading…</div> : !d.rows.length
        ? <div className="text-sm text-[var(--text-dim)] py-3">{tab === "pending" ? "Nothing waiting for approval." : "No requests yet."}</div>
        : d.rows.map((r) => <RequestRow key={r._id} r={r} admin={admin} onDone={done} />)}
    </section>
  );
}

const Stat = ({ label, v, tone, testid }) => (
  <div className="min-w-[92px]" data-testid={testid}>
    <div className={`text-lg font-semibold tabular-nums ${tone || ""}`}>{v ?? "\u2014"}</div>
    <div className="text-[10px] uppercase tracking-wider text-[var(--text-dim)]">{label}</div>
  </div>
);

function Person({ p, days }) {
  const [open, setOpen] = useState(false);
  const pct = (x) => (x == null ? null : `${x}%`);
  const bad = (x, lim) => (x != null && x < lim ? "text-[var(--breach)]" : "");
  return (
    <section className="panel p-4" data-testid={`person-${p.email}`}>
      <div className="flex flex-wrap items-baseline gap-x-3 mb-3">
        <div className="text-base font-medium">{p.name || p.email}</div>
        <div className="text-xs text-[var(--text-faint)]">{p.email} · last active {p.last_active ? fmtDateTime(p.last_active) : "never"}</div>
        <div className={`ml-auto text-xs inline-flex items-center gap-1 ${p.flags.length ? "text-[var(--breach)]" : "text-[var(--ok)]"}`}>
          {p.flags.length ? <><AlertTriangle size={12} />{p.flags.length} red flag{p.flags.length > 1 ? "s" : ""}</> : <><ShieldCheck size={12} />No red flags</>}
        </div>
      </div>
      <div className="flex flex-wrap gap-x-6 gap-y-3">
        <Stat label="Tasks done" v={p.tasks_done} testid={`stat-${p.email}-done`} />
        <Stat label="On time" v={pct(p.on_time_pct)} tone={bad(p.on_time_pct, 70)} />
        <Stat label="Done with proof" v={pct(p.done_with_evidence_pct)} tone={bad(p.done_with_evidence_pct, 80)} testid={`stat-${p.email}-evidence`} />
        <Stat label="Due today" v={p.due_today} tone={p.due_today ? "text-[var(--warn)]" : ""} />
        <Stat label="Overdue" v={p.overdue} tone={p.overdue ? "text-[var(--breach)]" : ""} />
        <Stat label="Open tasks" v={p.open_tasks} />
        <Stat label="Leads owned" v={p.leads_owned} />
        <Stat label="Untouched 3d+" v={p.untouched_leads} tone={p.untouched_leads ? "text-[var(--breach)]" : ""} />
        <Stat label="Calls" v={p.calls} /><Stat label="Emails" v={p.emails} /><Stat label="Notes" v={p.notes} />
        <Stat label="Meets booked" v={p.meetings_booked} /><Stat label="Requests" v={p.requests} />
      </div>
      {p.flags.length > 0 && (
        <div className="mt-4 space-y-2" data-testid={`flags-${p.email}`}>
          {p.flags.map((f) => <FlagCard key={f.kind} f={f} email={p.email} />)}
        </div>
      )}
      <button className="btn text-xs mt-4" onClick={() => setOpen(!open)} data-testid={`history-toggle-${p.email}`}>{open ? "Hide history" : "Show full history of tasks and activity"}</button>
      {open && <PersonHistory email={p.email} days={days} />}
    </section>
  );
}

export default function AdminDesk() {
  const { user } = useAuth();
  const admin = user?.role === "admin";
  const [days, setDays] = useState(7);
  const [d, setD] = useState(null);
  const [tab, setTab] = useState(() => localStorage.getItem("desk-tab") || "team");
  const pick = (t) => { setTab(t); localStorage.setItem("desk-tab", t); };
  useEffect(() => {
    if (!admin) return;
    setD(null);
    api.get("/governance/overview", { params: { days } }).then((r) => setD(r.data));
  }, [days, admin]);
  return (
    <div className="p-4 md:p-6 space-y-4 max-w-6xl" data-testid="admin-desk-page">
      <div className="flex flex-wrap items-baseline gap-3">
        <h1 className="text-2xl font-semibold">{admin ? "Admin desk" : "My requests"}</h1>
        {admin && [["team", "Team"], ["roas", "Ad ROAS"]].map(([k, l]) => (
          <button key={k} className={`text-sm ${tab === k ? "font-medium border-b-2 border-[var(--ai)]" : "text-[var(--text-dim)]"}`} onClick={() => pick(k)} data-testid={`desk-tab-${k}`}>{l}</button>
        ))}
        {admin && tab === "team" && <div className="ml-auto flex gap-1">{[1, 7, 30].map((n) => <button key={n} className={`btn text-xs ${days === n ? "btn-primary" : ""}`} onClick={() => setDays(n)} data-testid={`desk-days-${n}`}>{n === 1 ? "Today" : `${n} days`}</button>)}</div>}
      </div>
      {admin && tab === "roas" ? <RoasPanel /> : <>
      <Requests admin={admin} />
      {admin && (!d ? <div className="text-xs text-[var(--text-dim)]">Loading team…</div>
        : d.people.length ? d.people.map((p) => <Person key={p.email} p={p} days={days} />)
          : <div className="panel p-4 text-sm text-[var(--text-dim)]">No salespeople yet. Add team members in Settings → Team accounts.</div>)}
      </>}
    </div>
  );
}
